import React, { useEffect, useState } from 'react';
import {
  Box, TextField, Container, Grid, Card, CardContent, Divider, Button, FormControl, InputLabel, MenuItem, Select, Typography, styled, FormLabel, Tooltip, Radio, RadioGroup, FormControlLabel,
  Snackbar, Alert,
  Checkbox,
  ListItemText
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DateTimePicker from '@mui/lab/DateTimePicker';
import axios from 'axios';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { authenticationService } from "src/services";
import { multy_post,get } from 'src/services/httpClient.service';
import { useSnackbar } from 'src/components/snackbar/useSnackbar';

const StyledButton = styled(Button)({
  backgroundColor: '#1976D2',
  color: 'white',
  borderRadius: '4px',
  padding: '8px 12px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#1565C0',
  },
});

const AdContentFormats = ['Static Image', 'Animated Image (GIF)', 'Video', 'Interactive HTML5'];
const TargetPlaces = ['BANNER', 'INFOBAR', 'OVERLAY'];

const APIURL = process.env.REACT_APP_BASE_API_URL;

const AddAdvertisement = ({ handleClose, fetchAdvertisements }) => {
  const { showSnackbar } = useSnackbar();
  const { vendorId, franchiseId } = authenticationService.getVendorFranchiseIds();

  /* channel states */
  const [channels, setChannels] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState([]);
  
  const [formValues, setFormValues] = useState({
    fileType: 'Video',
    file: null,
    advertisementTime: '',
    targetPlatform: '',
    priorityType: '',
    startDate: null,
    endDate: null,
    timeRange: '',
    playDate: null,
    screenPosition: '',
    contentFormat: '',
    targetPlace: '',
    status: 'ACTIVE'
  });

  const [errors, setErrors] = useState({
    file: false,
    targetPlatform: false,
    priorityType: false,
    startDate: false,
    endDate: false,
    ///screenPosition: false,
    contentFormat: false,
    targetPlace: false,
  });

  const [touched, setTouched] = useState({
    file: false,
    targetPlatform: false,
    priorityType: false,
    startDate: false,
    endDate: false,
    //screenPosition: false,
    contentFormat: false,
    targetPlace: false,
  });

  const validateField = (name, value) => {
    console.log(name, value)
    switch (name) {
      case 'file':
        return value === null || value === undefined;
      case 'targetPlatform':
        return !value || value === '';
      case 'priorityType':
        return !value || value === '';
      case 'startDate':
        return !value || value === null;
      case 'endDate':
        return !value || value === null;
      // case 'screenPosition':
      //   return !value || value === '';
      case 'contentFormat':
        return !value || value === '';
      case 'targetPlace':
        return !value || value === '';
      default:
        return false;
    }
  };

  const validateDates = () => {
    if (formValues.startDate && formValues.endDate) {
      const start = new Date(formValues.startDate);
      const end = new Date(formValues.endDate);
      return end > start;
    }
    return true;
  };

  const validateForm = () => {
    const newErrors = {
      file: validateField('file', formValues.file),
      targetPlatform: validateField('targetPlatform', formValues.targetPlatform),
      priorityType: validateField('priorityType', formValues.priorityType),
      startDate: validateField('startDate', formValues.startDate),
      endDate: validateField('endDate', formValues.endDate),
      //screenPosition: validateField('screenPosition', formValues.screenPosition),
      contentFormat: validateField('contentFormat', formValues.contentFormat),
      targetPlace: validateField('targetPlace', formValues.targetPlace),
    };
    console.log(formValues)
    const datesValid = validateDates();
    if (!datesValid) {
      showSnackbar('End date must be after start date', 'error');
      return false;
    }

    setErrors(newErrors);

    // Mark all fields as touched
    setTouched({
      file: true,
      targetPlatform: true,
      priorityType: true,
      startDate: true,
      endDate: true,
      //screenPosition: true,
      contentFormat: true,
      targetPlace: true,
    });
    console.log(newErrors)
    // Check if any field is invalid
    return !Object.values(newErrors).some(error => error === true);
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormValues({ ...formValues, [id]: value });
    
    // Validate on change
    if (touched[id]) {
      setErrors({
        ...errors,
        [id]: validateField(id, value)
      });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormValues({ ...formValues, file });
    
    if (touched.file) {
      setErrors({
        ...errors,
        file: validateField('file', file)
      });
    }
  };

  const handlePlatformChange = (e) => {
    const { value } = e.target;
    setFormValues({
      ...formValues,
      targetPlatform: value,
    });
    
    if (touched.targetPlatform) {
      setErrors({
        ...errors,
        targetPlatform: validateField('targetPlatform', value)
      });
    }
  };

  const handleRevenueTypeChange = (e) => {
    const value = e.target.value;
    setFormValues({ ...formValues, priorityType: value });
    
    if (touched.priorityType) {
      setErrors({
        ...errors,
        priorityType: validateField('priorityType', value)
      });
    }
  };

  const handleDateChange = (date) => {
    if (!date) {
      setFormValues({ ...formValues, startDate: null });
      if (touched.startDate) {
        setErrors({
          ...errors,
          startDate: true
        });
      }
      return;
    }
    const formatted = format(date, "yyyy-MM-dd'T'HH:mm:ss", { locale: enUS });
    setFormValues({ ...formValues, startDate: formatted });
    
    if (touched.startDate) {
      setErrors({
        ...errors,
        startDate: false
      });
    }
  };

  const handleEndDateChange = (date) => {
    if (!date) {
      setFormValues({ ...formValues, endDate: null });
      if (touched.endDate) {
        setErrors({
          ...errors,
          endDate: true
        });
      }
      return;
    }
    const formatted = format(date, "yyyy-MM-dd'T'HH:mm:ss", { locale: enUS });
    setFormValues({ ...formValues, endDate: formatted });
    
    if (touched.endDate) {
      setErrors({
        ...errors,
        endDate: false
      });
    }
  };

  const handlePositionOnScreenChange = (e) => {
    const value = e.target.value;
    setFormValues({ ...formValues, screenPosition: value });
    
    // if (touched.screenPosition) {
    //   setErrors({
    //     ...errors,
    //     screenPosition: validateField('screenPosition', value)
    //   });
    // }
  };

  const handleFileTypeChange = (e) => {
    const value = e.target.value;
    setFormValues({ 
      ...formValues, 
      fileType: value,
      file: null // Reset file when changing type
    });
    
    if (touched.file) {
      setErrors({
        ...errors,
        file: true // File becomes invalid until new file is selected
      });
    }
  };

  const handleAdContentFormatChange = (e) => {
    const value = e.target.value;
    setFormValues({ ...formValues, contentFormat: value });
    
    if (touched.contentFormat) {
      setErrors({
        ...errors,
        contentFormat: validateField('contentFormat', value)
      });
    }
  };

  const handleTargetPlaceChange = (e) => {
    const value = e.target.value;
    setFormValues({ ...formValues, targetPlace: value });
    
    if (touched.targetPlace) {
      setErrors({
        ...errors,
        targetPlace: validateField('targetPlace', value)
      });
    }
  };

  const handleBlur = (fieldName) => {
    setTouched({
      ...touched,
      [fieldName]: true
    });
    
    setErrors({
      ...errors,
      [fieldName]: validateField(fieldName, formValues[fieldName])
    });
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      showSnackbar('Please fill in all mandatory fields', 'error');
      return;
    }

    try {
      const formData = new FormData();

      if (formValues.fileType === 'Video') {
        formData.append('video', formValues.file);
      } else if (formValues.fileType === 'Thumbnail') {
        formData.append('thumbnail', formValues.file);
      }

      const advertisementDto = {
        advertisementTime: formValues.advertisementTime,
        targetPlatform: formValues.targetPlatform,
        priorityType: formValues.priorityType,
        timeRange: formValues.timeRange,
        playDate: formValues.playDate,
        //screenPosition: formValues.screenPosition,
        contentFormat: formValues.contentFormat,
        targetPlace: formValues.targetPlace,
        vendorId: vendorId,
        franchiseId: franchiseId,
        startDate: formValues.startDate,
        endDate: formValues.endDate,
        status: formValues.status,
        channelIds: selectedChannels
      };
      console.log("advertisementDto-", advertisementDto)
      const advertisementDtoBlob = new Blob([JSON.stringify(advertisementDto)], {
        type: 'application/json',
      });
      formData.append('advertisementDTO', advertisementDtoBlob);

      const response = await multy_post(
        `${APIURL}/api/advertisements/upload`,
        formData
      );

      console.log('response',response)

      if (response?.data?.id && response?.data.id > 0) {
        showSnackbar('Added Advertisement Successfully', 'success');
        fetchAdvertisements();
        setTimeout(() => handleClose(), 800);
      } else {
        showSnackbar('Failed to add advertisement', 'error');
      }
    } catch (error) {
      showSnackbar(error.message || 'Error adding advertisement', 'error');
    }
  };

  /* channellist function */


useEffect(() => {
  const fetchChannels = async () => {
    try {
      const response = await get(
        `${APIURL}/channel/vendor/${vendorId}/findAll`
      );

      const channelList =
        (response?.channelList ?? []).flatMap(group =>
          (group.channelData ?? []).map(ch => ({
            id: ch.id,
            name: ch.channelName
          }))
        );
       console.log('response',channelList)
      setChannels(channelList);
      console.log("channelList :", channels)

    } catch (error) {
      console.error("Error fetching channels:", error);
    }
  };

  fetchChannels();
}, []);


const handleChannelChange = (event) => {
  const value = event.target.value;
  setSelectedChannels(value);
};

  return (
    <>
      <Helmet>
        <title>Advertisement Page</title>
      </Helmet>
      <Container maxWidth="lg">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card sx={{ boxShadow: 3 }}>
              <Divider />
              <CardContent>
                <Box sx={{ marginBottom: '20px' }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <FormControl component="fieldset" error={touched.file && errors.file}>
                        <FormLabel component="legend" sx={{ color: touched.file && errors.file ? 'error.main' : 'inherit' }}>
                          Select File Type <span style={{ color: 'red' }}>*</span>
                        </FormLabel>
                        <RadioGroup
                          id="fileType"
                          name="fileType"
                          value={formValues.fileType}
                          onChange={handleFileTypeChange}
                          row
                        >
                          <FormControlLabel
                            value="Video"
                            control={<Radio />}
                            label="Video"
                          />
                          <FormControlLabel
                            value="Thumbnail"
                            control={<Radio />}
                            label="Thumbnail"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <input
                        accept={formValues.fileType === 'Video' ? 'video/*' : 'image/*'}
                        id="file"
                        type="file"
                        onChange={handleFileChange}
                        onBlur={() => handleBlur('file')}
                        style={{ display: 'none' }}
                      />

                      <label htmlFor="file">
                        <Tooltip title={`${formValues.fileType} file (Required)`}>
                          <StyledButton 
                            as="span"
                            sx={{ 
                              backgroundColor: touched.file && errors.file ? '#d32f2f' : '#1976D2',
                              '&:hover': {
                                backgroundColor: touched.file && errors.file ? '#b71c1c' : '#1565C0',
                              }
                            }}
                          >
                            {formValues.file ? `${formValues.file.name} selected` : `Select ${formValues.fileType} *`}
                          </StyledButton>
                        </Tooltip>
                      </label>
                      {touched.file && errors.file && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                          {formValues.fileType} file is required
                        </Typography>
                      )}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth error={touched.targetPlatform && errors.targetPlatform}>
                        <InputLabel id="targetPlatform-label">
                          Target Platform <span style={{ color: 'red' }}>*</span>
                        </InputLabel>
                        <Select
                          labelId="targetPlatform-label"
                          id="targetPlatform"
                          value={formValues.targetPlatform}
                          onChange={handlePlatformChange}
                          onBlur={() => handleBlur('targetPlatform')}
                          label="Target Platform *"
                        >
                          <MenuItem value="">
                            <em>Select Platform</em>
                          </MenuItem>
                          <MenuItem value="TV">TV</MenuItem>
                          <MenuItem value="MOBILE">Mobile</MenuItem>
                        </Select>
                        {touched.targetPlatform && errors.targetPlatform && (
                          <Typography variant="caption" color="error">
                            Target platform is required
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth error={touched.targetPlace && errors.targetPlace}>
                        <InputLabel id="targetPlace-label">
                          Target Place <span style={{ color: 'red' }}>*</span>
                        </InputLabel>
                        <Select
                          labelId="targetPlace-label"
                          id="targetPlace"
                          value={formValues.targetPlace}
                          onChange={handleTargetPlaceChange}
                          onBlur={() => handleBlur('targetPlace')}
                          label="Target Place *"
                        >
                          <MenuItem value="">
                            <em>Select Target Place</em>
                          </MenuItem>
                          {TargetPlaces.map((place) => (
                            <MenuItem key={place} value={place}>
                              {place}
                            </MenuItem>
                          ))}
                        </Select>
                        {touched.targetPlace && errors.targetPlace && (
                          <Typography variant="caption" color="error">
                            Target place is required
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
  <FormControl fullWidth>
    <InputLabel id="channels-label">
      Channels (Optional)
    </InputLabel>

    <Select
  labelId="channels-label"
  multiple
  value={selectedChannels}
  onChange={handleChannelChange}
  label="Channels (Optional)"
  renderValue={(selected) =>
    selected
      .map((id) => channels.find(c => c.id === id)?.name)
      .join(", ")
  }
>
  {channels.map((channel) => (
    <MenuItem key={channel.id} value={channel.id}>
      <Checkbox checked={selectedChannels.indexOf(channel.id) > -1} />
      <ListItemText primary={channel.name} />
    </MenuItem>
  ))}
</Select>
  </FormControl>
</Grid>
                    
                    <Grid item xs={12}>
                      <FormControl component="fieldset" error={touched.priorityType && errors.priorityType}>
                        <FormLabel component="legend" sx={{ color: touched.priorityType && errors.priorityType ? 'error.main' : 'inherit' }}>
                          Priority Type <span style={{ color: 'red' }}>*</span>
                        </FormLabel>
                        <RadioGroup
                          id="priorityType"
                          name="priorityType"
                          value={formValues.priorityType}
                          onChange={handleRevenueTypeChange}
                          onBlur={() => handleBlur('priorityType')}
                          row
                        >
                          <FormControlLabel
                            value="10"
                            control={<Radio />}
                            label="High"
                          />
                          <FormControlLabel
                            value="5"
                            control={<Radio />}
                            label="Medium"
                          />
                          <FormControlLabel
                            value="1"
                            control={<Radio />}
                            label="Low"
                          />
                        </RadioGroup>
                        {touched.priorityType && errors.priorityType && (
                          <Typography variant="caption" color="error">
                            Revenue type is required
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DateTimePicker
                          label="Start Date & Time *"
                          value={formValues.startDate}
                          onChange={handleDateChange}
                          //onBlur={() => handleBlur('startDate')}
                          renderInput={(params) => (
                            <TextField 
                              {...params} 
                              fullWidth 
                              error={touched.startDate && errors.startDate}
                              helperText={touched.startDate && errors.startDate ? 'Start date is required' : ''}
                              required
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DateTimePicker
                          label="End Date & Time *"
                          value={formValues.endDate}
                          onChange={handleEndDateChange}
                          //onBlur={() => handleBlur('endDate')}
                          renderInput={(params) => (
                            <TextField 
                              {...params} 
                              fullWidth 
                              error={touched.endDate && errors.endDate}
                              helperText={touched.endDate && errors.endDate ? 'End date is required' : ''}
                              required
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </Grid>

                    {/* <Grid item xs={6}>
                      <FormControl fullWidth error={touched.screenPosition && errors.screenPosition}>
                        <InputLabel id="positionOnScreen-label">
                          Position on the Screen <span style={{ color: 'red' }}>*</span>
                        </InputLabel>
                        <Select
                          labelId="positionOnScreen-label"
                          id="screenPosition"
                          value={formValues.screenPosition}
                          onChange={handlePositionOnScreenChange}
                          onBlur={() => handleBlur('screenPosition')}
                          label="Position on the Screen *"
                        >
                          <MenuItem value="">
                            <em>Select Position</em>
                          </MenuItem>
                          <MenuItem value="top">Top</MenuItem>
                          <MenuItem value="bottom">Bottom</MenuItem>
                          <MenuItem value="left">Left</MenuItem>
                          <MenuItem value="right">Right</MenuItem>
                        </Select>
                        {touched.screenPosition && errors.screenPosition && (
                          <Typography variant="caption" color="error">
                            Screen position is required
                          </Typography>
                        )}
                      </FormControl>
                    </Grid> */}

                    <Grid item xs={6}>
                      <FormControl fullWidth error={touched.contentFormat && errors.contentFormat}>
                        <InputLabel id="contentFormat-label">
                          Ad Content Format <span style={{ color: 'red' }}>*</span>
                        </InputLabel>
                        <Select
                          labelId="contentFormat-label"
                          id="contentFormat"
                          value={formValues.contentFormat}
                          onChange={handleAdContentFormatChange}
                          onBlur={() => handleBlur('contentFormat')}
                          label="Ad Content Format *"
                        >
                          <MenuItem value="">
                            <em>Select Format</em>
                          </MenuItem>
                          {AdContentFormats.map((format) => (
                            <MenuItem key={format} value={format}>
                              {format}
                            </MenuItem>
                          ))}
                        </Select>
                        {touched.contentFormat && errors.contentFormat && (
                          <Typography variant="caption" color="error">
                            Ad content format is required
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <InputLabel id="status">
                          Status
                        </InputLabel>
                        <Select
                          labelId="status"
                          id="status"
                          value={formValues.status}
                          onBlur={() => handleBlur('status')}
                          label="Status"
                        >
                          <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                          <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                  <Button
                    sx={{ marginRight: '10px' }}
                    onClick={handleClose}
                    variant="outlined"
                    color="primary"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                  >
                    Submit
                  </Button>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    * Required fields
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default AddAdvertisement;